const CPF_LENGTH = 11;
const MINIMUM_AGE = 18;
const MAXIMUM_AGE = 100;

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 150;

const CRF_MIN_LENGTH = 1;
const CRF_MAX_LENGTH = 5;

const EMAIL_MAX_LENGTH = 254;

const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 128;

const VALID_BRAZILIAN_DDDS = new Set([
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '21', '22', '24', '27', '28', '31', '32', '33', '34', '35', '37', '38', '41', '42', '43', '44', '45', '46',
  '47', '48', '49', '51', '53', '54', '55', '61', '62', '64', '63', '65', '66', '67', '68', '69', '71', '73', '74', '75', '77', '79', '81', '87', '82', '83', '84', 
  '85', '88', '86', '89', '91', '93', '94', '92', '97', '95', '96', '98', '99',
]);


export const createRequiredText = (
  fieldName?: string,
): string => (
  fieldName
    ? `${fieldName} é obrigatório`
    : 'Obrigatório'
);

export const checkExactLength = (
  value: string,
  length: number,
  message?: string,
): boolean | string => (
  value?.length === length
  || message
  || 'Tamanho inválido'
);

export const checkOnlyNumber = (
  value: string,
  message?: string,
): boolean | string => (
  /^\d+$/.test(value ?? '')
  || message
  || 'Somente números'
);

export const checkOnlyString = (
  value: string,
  message?: string,
): boolean | string => (
  !/\d/.test(value ?? '')
  || message
  || 'Somente letras'
);

export const validateCpf = (
  value: string,
): boolean | string => {
  const cpf = value?.replace(/\D/g, '') ?? '';

  if (cpf.length !== CPF_LENGTH) {
    return 'CPF deve ter 11 números';
  }

  if (/^(\d)\1{10}$/.test(cpf)) {
    return 'CPF inválido';
  }

  const calculateDigit = (
    digits: string,
    initialWeight: number,
  ): number => {
    const sum = digits
      .split('')
      .reduce(
        (total, digit, index) => (
          total + Number(digit) * (initialWeight - index)
        ),
        0,
      );

    const remainder = sum % 11;

    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calculateDigit(
    cpf.substring(0, 9),
    10,
  );

  if (firstDigit !== Number(cpf[9])) {
    return 'CPF inválido';
  }

  const secondDigit = calculateDigit(
    cpf.substring(0, 10),
    11,
  );

  if (secondDigit !== Number(cpf[10])) {
    return 'CPF inválido';
  }

  return true;
};

export const validateDate = (
  value: string,
): boolean | string => {
  const datePattern =
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/;

  if (!datePattern.test(value ?? '')) {
    return 'Data inválida';
  }

  const [day, month, year] = value
    .split('/')
    .map(Number);

  const birthDate = new Date(
    year,
    month - 1,
    day,
  );

  birthDate.setHours(0, 0, 0, 0);

  if (
    birthDate.getFullYear() !== year
    || birthDate.getMonth() !== month - 1
    || birthDate.getDate() !== day
  ) {
    return 'Data inválida';
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (birthDate > today) {
    return 'Data inválida';
  }

  let age =
    today.getFullYear()
    - birthDate.getFullYear();

  const birthdayHasNotOccurred =
    today.getMonth() < birthDate.getMonth()
    || (
      today.getMonth() === birthDate.getMonth()
      && today.getDate() < birthDate.getDate()
    );

  if (birthdayHasNotOccurred) {
    age -= 1;
  }

  if (
    age < MINIMUM_AGE
    || age > MAXIMUM_AGE
  ) {
    return 'Data inválida';
  }

  return true;
};

export const validateName = (
  value: string,
): boolean | string => {
  const normalizedName = value
    ?.trim()
    .replace(/\s+/g, ' ') ?? '';

  if (!normalizedName) {
    return createRequiredText();
  }

  if (normalizedName.length < NAME_MIN_LENGTH) {
    return `O nome deve ter pelo menos ${NAME_MIN_LENGTH} caracteres`;
  }

  if (normalizedName.length > NAME_MAX_LENGTH) {
    return `O nome deve ter no máximo ${NAME_MAX_LENGTH} caracteres`;
  }

  if (
    !/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(normalizedName)
    || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(normalizedName)
  ) {
    return 'Nome inválido';
  }

  return true;
};

export const validateCrf = (
  value: string,
): boolean | string => {
  const crf = value?.trim() ?? '';

  if (!crf) {
    return createRequiredText();
  }

  if (!/^\d+$/.test(crf)) {
    return 'CRF deve conter somente números';
  }

  if (
    crf.length < CRF_MIN_LENGTH
    || crf.length > CRF_MAX_LENGTH
  ) {
    return `CRF deve ter no máximo ${CRF_MAX_LENGTH} números`;
  }

  if (/^0+$/.test(crf)) {
    return 'CRF inválido';
  }

  return true;
};

export const validateDdd = (
  value: string,
): boolean | string => {
  const ddd = value?.replace(/\D/g, '') ?? '';

  if (ddd.length !== 2) {
    return 'DDD deve ter 2 números';
  }

  if (!/^\d{2}$/.test(ddd)) {
    return 'DDD deve conter somente números';
  }

  if (!VALID_BRAZILIAN_DDDS.has(ddd)) {
    return 'DDD inválido';
  }

  return true;
};

export const validatePhoneNumber = (
  value: string,
): boolean | string => {
  const phone = value?.replace(/\D/g, '') ?? '';

  if (phone.length !== 9) {
    return 'Celular deve ter 9 números';
  }

  if (!/^\d{9}$/.test(phone)) {
    return 'Celular deve conter somente números';
  }

  if (!phone.startsWith('9')) {
    return 'Celular deve iniciar com o número 9';
  }

  const subscriberNumber = phone.substring(1);

  if (/^(\d)\1{7}$/.test(subscriberNumber)) {
    return 'Celular inválido';
  }

  return true;
};

export const validateEmail = (
  value: string,
): boolean | string => {
  const email = value?.trim() ?? '';

  if (!email) {
    return createRequiredText();
  }

  if (email.length > EMAIL_MAX_LENGTH) {
    return `O e-mail deve ter no máximo ${EMAIL_MAX_LENGTH} caracteres`;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  if (!emailPattern.test(email)) {
    return 'E-mail inválido';
  }

  return true;
};

export const validatePassword = (
  value: string,
): boolean | string => {
  const password = value ?? '';

  if (!password) {
    return createRequiredText();
  }

  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Deve ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres`;
  }

  if (password.length > PASSWORD_MAX_LENGTH) {
    return `A senha deve ter no máximo ${PASSWORD_MAX_LENGTH} caracteres`;
  }

  return true;
};

export const validateBothCheckedTerms = (
  value: string,
  allValues: any,
): boolean | string => {
  if (
    !allValues.confirmation
    || !allValues.terms
  ) {
    return 'Para finalizar o cadastro, você deverá aceitar todos os termos';
  }

  return true;
};

export const validateEqualPasswords = (
  value: string,
  allValues: any,
): boolean | string => {
  if (allValues.password !== allValues.confirmPassword) {
    return 'As senhas não correspondem';
  }

  return true;
};
